import React, { useEffect, useState } from 'react';
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import db from './Firebase';  // adjust import path if needed
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from './Firebase';

const TABS = [
    "Application Form",
    "Declaration from Parent",
    "FOR OFFICE USE ONLY",
    "Upload Documents"
];

const LOCAL_STORAGE_KEY = 'multiTabFormData';

const TabForm = () => {
    const [currentTab, setCurrentTab] = useState(0);
    const [formData, setFormData] = useState({
        // Tab 1
        applicantName: '',
        course: '',
        batch: '',
        dob: '',
        doa: '',
        age: '',
        gender: '',
        qualification: '',
        contact1: '',
        address: '',
        fatherName: '',
        contact2: '',
        fatherOccupation: '',
        motherName: '',
        motherOccupation: '',
        familySize: '',
        familyType: '',
        income: '',
        group: '',
        // Change documents to an array for multi‑select
        documents: [],
        jobPreference: '',
        date: '',
        photo: null,
        signature: null,
        // Tab 2
        declarationName: '',
        declarationChildName: '',
        declarationCourse: '',
        declarationDate: '',
        declarationSignature: null,
        // Tab 3
        centre: "",
        referredBy: "",
        regNo: "",
        batchNo: "",
        docsSubmitted: "",
        screenSchedule: "",
        officeDate: "",
        sswa: "",
        sswp: "",
        inChargeName: "",
        inChargeSignature: null,
        // Tab 4 (Upload Documents)
        uploadDoc1: null,
        uploadDoc2: null,
        uploadDoc3: null,
        uploadDoc4: null,
        uploadDoc5: null,
        uploadDoc6: null,

        approver: "",
        status: "Pending",
        remark: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // Load from localStorage on mount
    useEffect(() => {
        const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (saved) {
            setFormData(JSON.parse(saved));
        }
    }, []);

    const handleChange = (e) => {
        const { name, value, files, type, multiple, selectedOptions } = e.target;

        if (files) {
            // Single file input
            setFormData(prev => ({
                ...prev,
                [name]: files[0]
            }));
        } else if (type === 'select-multiple') {
            // Multi‑select: convert options to array of values
            const values = Array.from(selectedOptions).map(opt => opt.value);
            setFormData(prev => ({
                ...prev,
                [name]: values
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSave = () => {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(formData));
        alert(`✅ Saved: ${TABS[currentTab]}`);
    };

    const handleFinalSubmit = async () => {
        try {
            setIsSubmitting(true);
            setSubmitError(null);
            setSubmitSuccess(false);

            const uploadFileAndGetURL = async (file, folder = "form_uploads") => {
                if (!file) return null;
                const storageRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
                const uploadTask = await uploadBytesResumable(storageRef, file);
                return await getDownloadURL(uploadTask.ref);
            };

            // Upload files. We need to upload:
            // photo, signature, declarationSignature, inChargeSignature
            // plus the 6 uploadDoc# files (optional).
            const uploadPromises = [
                uploadFileAndGetURL(formData.photo),
                uploadFileAndGetURL(formData.signature),
                uploadFileAndGetURL(formData.declarationSignature),
                uploadFileAndGetURL(formData.inChargeSignature),
                uploadFileAndGetURL(formData.uploadDoc1, "uploads"),
                uploadFileAndGetURL(formData.uploadDoc2, "uploads"),
                uploadFileAndGetURL(formData.uploadDoc3, "uploads"),
                uploadFileAndGetURL(formData.uploadDoc4, "uploads"),
                uploadFileAndGetURL(formData.uploadDoc5, "uploads"),
                uploadFileAndGetURL(formData.uploadDoc6, "uploads"),
            ];

            const [
                photoURL,
                signatureURL,
                declarationSignatureURL,
                inChargeSignatureURL,
                doc1URL,
                doc2URL,
                doc3URL,
                doc4URL,
                doc5URL,
                doc6URL
            ] = await Promise.all(uploadPromises);

            const payload = {
                ...formData,
                photo: photoURL,
                signature: signatureURL,
                declarationSignature: declarationSignatureURL,
                inChargeSignature: inChargeSignatureURL,
                uploadDoc1: doc1URL,
                uploadDoc2: doc2URL,
                uploadDoc3: doc3URL,
                uploadDoc4: doc4URL,
                uploadDoc5: doc5URL,
                uploadDoc6: doc6URL,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            // Save to Firestore
            await addDoc(collection(db, "registration_form_data"), payload);

            // Clear localStorage and reset form
            localStorage.removeItem(LOCAL_STORAGE_KEY);
            setFormData({
                applicantName: '',
                course: '',
                batch: '',
                dob: '',
                doa: '',
                age: '',
                gender: '',
                qualification: '',
                contact1: '',
                address: '',
                fatherName: '',
                contact2: '',
                fatherOccupation: '',
                motherName: '',
                motherOccupation: '',
                familySize: '',
                familyType: '',
                income: '',
                group: '',
                documents: [],
                jobPreference: '',
                date: '',
                photo: null,
                signature: null,
                declarationName: '',
                declarationChildName: '',
                declarationCourse: '',
                declarationDate: '',
                declarationSignature: null,
                centre: '',
                referredBy: '',
                regNo: '',
                batchNo: '',
                docsSubmitted: '',
                screenSchedule: '',
                officeDate: '',
                sswa: "",
                sswp: "",
                inChargeName: '',
                inChargeSignature: null,
                uploadDoc1: null,
                uploadDoc2: null,
                uploadDoc3: null,
                uploadDoc4: null,
                uploadDoc5: null,
                uploadDoc6: null,
                approver: "",
                status: "Pending",
                remark: "",
            });
            setCurrentTab(0);
            setSubmitSuccess(true);
        } catch (error) {
            console.error("Error during final submit:", error);
            setSubmitError("Error submitting form. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNext = () => {
        if (currentTab < TABS.length - 1) setCurrentTab(prev => prev + 1);
    };

    const handlePrev = () => {
        if (currentTab > 0) setCurrentTab(prev => prev - 1);
    };

    const inputStyle = { width: "100%", padding: "10px", marginBottom: "20px", boxSizing: "border-box" };
    const labelStyle = { marginBottom: "5px", display: "block", fontWeight: 500 };

    return (
        <div style={{ maxWidth: "auto", margin: '0 auto', padding: 20, fontFamily: 'Arial, sans-serif' }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                {TABS.map((tab, idx) => (
                    <button key={idx}
                        onClick={() => setCurrentTab(idx)}
                        style={{
                            flex: 1,
                            padding: "10px 20px",
                            background: currentTab === idx ? "#4A90E2" : "#f0f0f0",
                            color: currentTab === idx ? "#fff" : "#333",
                            border: "none",
                            cursor: "pointer",
                            fontWeight: currentTab === idx ? "bold" : "normal",
                            borderRadius: 4
                        }}>
                        {tab}
                    </button>
                ))}
            </div>

            {isSubmitting && (
                <div style={{ marginBottom: 20, color: "#555", fontWeight: "bold" }}>
                    Uploading files, please wait...
                </div>
            )}
            {submitSuccess && (
                <div style={{ marginBottom: 20, color: "green", fontWeight: "bold" }}>
                    ✅ Successfully submitted!
                </div>
            )}
            {submitError && (
                <div style={{ marginBottom: 20, color: "red", fontWeight: "bold" }}>
                    ❌ {submitError}
                </div>
            )}

            <div>
                {/* Tab 1 */}
                {currentTab === 0 && (
                    <div className="card p-4 shadow-sm">
                        <h4 className="mb-4">Application Form</h4>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label htmlFor="applicantName" style={{ display: "flex" }}>Applicant Name</label>
                                <input type="text" name="applicantName" id="applicantName" value={formData.applicantName} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="course" style={{ display: "flex" }}>Course Applied For</label>
                                <select name="course" id="course" value={formData.course} onChange={handleChange} className="form-select">
                                    <option value="">Select Course</option>
                                    <option>Retail Sales Assistant</option>
                                    <option>Warehouse Associate</option>
                                    <option>Digital Mitra</option>
                                    <option>IT Customer Care Executive</option>
                                </select>
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="batch" style={{ display: "flex" }}>Preferred Batch</label>
                                <select name="batch" id="batch" value={formData.batch} onChange={handleChange} className="form-select">
                                    <option value="">Select Batch</option>
                                    <option>Morning</option>
                                    <option>Afternoon</option>
                                </select>
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="dob" style={{ display: "flex" }}>Date of Birth</label>
                                <input type="date" name="dob" id="dob" value={formData.dob} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="age" style={{ display: "flex" }}>Age</label>
                                <input type="number" name="age" id="age" value={formData.age} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="gender" style={{ display: "flex" }}>Gender</label>
                                <select name="gender" id="gender" value={formData.gender} onChange={handleChange} className="form-select">
                                    <option value="">Select Gender</option>
                                    <option>Male</option>
                                    <option>Female</option>
                                    <option>Other</option>
                                </select>
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="qualification" style={{ display: "flex" }}>Qualification</label>
                                <input type="text" name="qualification" id="qualification" value={formData.qualification} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="contact1" style={{ display: "flex" }}>Contact No.</label>
                                <input type="text" name="contact1" id="contact1" value={formData.contact1} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="address" style={{ display: "flex" }}>Address</label>
                                <input type="text" name="address" id="address" value={formData.address} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="fatherName" style={{ display: "flex" }}>Father’s Name</label>
                                <input type="text" name="fatherName" id="fatherName" value={formData.fatherName} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="contact2" style={{ display: "flex" }}>Father’s Contact</label>
                                <input type="text" name="contact2" id="contact2" value={formData.contact2} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="fatherOccupation" style={{ display: "flex" }}>Father’s Occupation</label>
                                <input type="text" name="fatherOccupation" id="fatherOccupation" value={formData.fatherOccupation} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="motherName" style={{ display: "flex" }}>Mother’s Name</label>
                                <input type="text" name="motherName" id="motherName" value={formData.motherName} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="motherOccupation" style={{ display: "flex" }}>Mother’s Occupation</label>
                                <input type="text" name="motherOccupation" id="motherOccupation" value={formData.motherOccupation} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="familySize" style={{ display: "flex" }}>Family Size</label>
                                <input type="number" name="familySize" id="familySize" value={formData.familySize} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="familyType" style={{ display: "flex" }}>Type of Family</label>
                                <select name="familyType" id="familyType" value={formData.familyType} onChange={handleChange} className="form-select">
                                    <option value="">Select Type</option>
                                    <option>Nuclear</option>
                                    <option>Joint</option>
                                </select>
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="income" style={{ display: "flex" }}>Monthly Income</label>
                                <input type="text" name="income" id="income" value={formData.income} onChange={handleChange} className="form-control" />
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="group" style={{ display: "flex" }}>Social Group</label>
                                <select name="group" id="group" value={formData.group} onChange={handleChange} className="form-select">
                                    <option value="">Select</option>
                                    <option>SC</option>
                                    <option>ST</option>
                                    <option>OBC</option>
                                    <option>General</option>
                                    <option>Others</option>
                                </select>
                            </div>

                            <div className="col-md-4">
                                <label htmlFor="jobPreference" style={{ display: "flex" }}>Employment on Completion</label>
                                <select name="jobPreference" id="jobPreference" value={formData.jobPreference} onChange={handleChange} className="form-select">
                                    <option value="">Select</option>
                                    <option>Yes</option>
                                    <option>No</option>
                                </select>
                            </div>


                            <div className="col-md-4">
                                <label htmlFor="doa" style={{ display: "flex" }}>Date of Application</label>
                                <input type="date" name="doa" id="doa" value={formData.doa} onChange={handleChange} className="form-control" />
                            </div>


                            <div className="col-md-4">
                                <label htmlFor="documents" style={labelStyle}>Documents Attached</label>
                                <select
                                    name="documents"
                                    id="documents"
                                    multiple
                                    value={formData.documents}
                                    onChange={handleChange}
                                    className="form-select"
                                    style={{ height: "auto" }}
                                >
                                    <option value="10th">10th</option>
                                    <option value="12th">12th</option>
                                    <option value="Graduation">Graduation</option>
                                    <option value="Aadhar Card">Aadhar Card</option>
                                    <option value="Pan Card">Pan Card</option>
                                    <option value="Other">Other</option>
                                </select>
                                <small className="text-muted">
                                    Selected: {formData.documents.join(", ") || "None"}
                                </small>
                            </div>

                            {/* Upload Photo */}
                            <div className="col-md-4">
                                <label htmlFor="photo" style={labelStyle}>Upload Photo</label>
                                <input
                                    type="file"
                                    name="photo"
                                    id="photo"
                                    accept="image/*"
                                    onChange={handleChange}
                                    className="form-control"
                                />
                                {formData.photo && <small className="text-muted">Selected: {formData.photo.name}</small>}
                            </div>

                            {/* Upload Signature */}
                            <div className="col-md-4">
                                <label htmlFor="signature" style={labelStyle}>Upload Signature</label>
                                <input
                                    type="file"
                                    name="signature"
                                    id="signature"
                                    accept="image/*"
                                    onChange={handleChange}
                                    className="form-control"
                                />
                                {formData.signature && <small className="text-muted">Selected: {formData.signature.name}</small>}
                            </div>

                            {/* ... rest of the fields unchanged ... */}
                        </div>
                    </div>
                )}

                {/* Tab 2 */}
                {currentTab === 1 && (
                    <div className="card p-4 shadow-sm">
                        <h4 className="mb-4">Declaration from Parent</h4>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Parent’s Name</label>
                                <input type="text" name="declarationName" value={formData.declarationName} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Child’s Name</label>
                                <input type="text" name="declarationChildName" value={formData.declarationChildName} onChange={handleChange} className="form-control" />
                            </div>
                            {/* <div className="col-md-4">
                                <label style={{ display: "flex" }}>Course (CSR Program)</label>
                                <input type="text" name="declarationCourse" value={formData.declarationCourse} onChange={handleChange} className="form-control" />
                            </div> */}
                            <div className="col-md-4">
                                <label htmlFor="declarationCourse" style={{ display: "flex" }}>Course (CSR Program)</label>
                                <select name="declarationCourse" id="declarationCourse" value={formData.declarationCourse} onChange={handleChange} className="form-select">
                                    <option value="">Select Course</option>
                                    <option>Retail Sales Assistant</option>
                                    <option>Warehouse Associate</option>
                                    <option>Digital Mitra</option>
                                    <option>IT Customer Care Executive</option>
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Date of Declaration</label>
                                <input type="date" name="declarationDate" value={formData.declarationDate} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Upload Parent’s Signature</label>
                                <input type="file" name="declarationSignature" accept="image/*" onChange={handleChange} className="form-control" />
                                {formData.declarationSignature && <small className="text-muted">Selected: {formData.declarationSignature.name}</small>}
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab 3 */}
                {currentTab === 2 && (
                    <div className="card p-4 shadow-sm">
                        <h4 className="mb-4">FOR OFFICE USE ONLY</h4>
                        <div className="row g-3">

                            {/* Training Centre */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Training Centre</label>
                                <select
                                    name="centre"
                                    value={formData.centre}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Select Centre</option>
                                    <option value="Mansa">Mansa</option>
                                    <option value="Bhiwandi">Bhiwandi</option>
                                    <option value="Tumkur">Tumkur</option>
                                </select>
                            </div>

                            {/* Referred By */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Referred By</label>
                                <select
                                    name="referredBy"
                                    value={formData.referredBy}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Select</option>
                                    <option value="Mobiliser">Mobiliser</option>
                                    <option value="Trainer">Trainer</option>
                                    <option value="Friend">Friend</option>
                                </select>
                            </div>

                            {/* Registration No */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Registration No.</label>
                                <input
                                    type="text"
                                    name="regNo"
                                    value={formData.regNo}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            {/* Batch No */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Batch No.</label>
                                <input
                                    type="text"
                                    name="batchNo"
                                    value={formData.batchNo}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            {/* Photocopy of required documents submitted */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Photocopy of Required Documents Submitted</label>
                                <select
                                    name="docsSubmitted"
                                    value={formData.docsSubmitted}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Select</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                </select>
                            </div>

                            {/* Screening Schedule */}
                            {/* <div className="col-md-4">
                                <label style={{ display: "flex" }}>Screening Schedule</label>
                                <select
                                    name="screenSchedule"
                                    value={formData.screenSchedule || ""}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Select</option>
                                    <option value="with applicant">With Applicant</option>
                                    <option value="with parents">With Parents</option>
                                </select>
                            </div> */}

                            {/* Office Date */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Screening Schedule - With Applicant</label>
                                <input
                                    type="date"
                                    name="sswa"
                                    value={formData.sswa}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Screening Schedule - With Parent</label>
                                <input
                                    type="date"
                                    name="sswp"
                                    value={formData.sswp}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Office Date</label>
                                <input
                                    type="date"
                                    name="officeDate"
                                    value={formData.officeDate}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            {/* Centre In‑Charge Name */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Centre In‑Charge Name</label>
                                <input
                                    type="text"
                                    name="inChargeName"
                                    value={formData.inChargeName || ""}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            {/* Upload Centre In‑Charge Signature */}
                            <div className="col-md-4">
                                <label style={{ display: "flex" }}>Upload Centre In‑Charge Signature</label>
                                <input
                                    type="file"
                                    name="inChargeSignature"
                                    accept="image/*"
                                    onChange={handleChange}
                                    className="form-control"
                                />
                                {formData.inChargeSignature && (
                                    <small className="text-muted">
                                        Selected: {formData.inChargeSignature.name || "1 file uploaded"}
                                    </small>
                                )}
                            </div>

                        </div>
                    </div>
                )}

                {/* Tab 4: Upload Documents */}
                {currentTab === 3 && (
                    <div className="card p-4 shadow-sm">
                        <h4 className="mb-4">Upload Documents</h4>
                        <div className="row g-3">
                            {[1, 2, 3, 4, 5, 6].map(i => (
                                <div key={i} className="col-md-4">
                                    <label htmlFor={`uploadDoc${i}`} style={labelStyle}>
                                        Upload Document {i}
                                    </label>
                                    <input
                                        type="file"
                                        name={`uploadDoc${i}`}
                                        id={`uploadDoc${i}`}
                                        accept="image/*,application/pdf"
                                        onChange={handleChange}
                                        className="form-control"
                                    />
                                    {formData[`uploadDoc${i}`] && (
                                        <small className="text-muted">
                                            Selected: {formData[`uploadDoc${i}`].name}
                                        </small>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Navigation Buttons */}
            <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                {currentTab > 0 && (
                    <button type="button" onClick={handlePrev} style={{ padding: "10px 20px", background: "#6c757d", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>
                        Previous
                    </button>
                )}
                <button type="button" onClick={handleSave} style={{ padding: "10px 20px", background: "#007bff", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>
                    Save
                </button>
                {currentTab < TABS.length - 1 ? (
                    <button type="button" onClick={handleNext} style={{ padding: "10px 20px", background: "#17a2b8", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>
                        Next
                    </button>
                ) : (
                    <button type="button" onClick={handleFinalSubmit} disabled={isSubmitting} style={{ padding: "10px 20px", background: "#28a745", color: "#fff", border: "none", borderRadius: 4, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                        {isSubmitting ? "Submitting…" : "Final Submit"}
                    </button>
                )}
            </div>
        </div>
    );
};

export default TabForm;






// import React, { useEffect, useState } from 'react';
// import { collection, addDoc, serverTimestamp } from "firebase/firestore";
// import db from './Firebase';  // adjust import path if needed
// import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
// import { storage } from './Firebase';

// const TABS = [
//     "Application Form",
//     "Declaration from Parent",
//     "FOR OFFICE USE ONLY"
// ];

// const LOCAL_STORAGE_KEY = 'multiTabFormData';

// const TabForm = () => {
//     const [currentTab, setCurrentTab] = useState(0);
//     const [formData, setFormData] = useState({
//         // Tab 1
//         applicantName: '',
//         course: '',
//         batch: '',
//         dob: '',
//         doa: '',
//         age: '',
//         gender: '',
//         qualification: '',
//         contact1: '',
//         address: '',
//         fatherName: '',
//         contact2: '',
//         fatherOccupation: '',
//         motherName: '',
//         motherOccupation: '',
//         familySize: '',
//         familyType: '',
//         income: '',
//         group: '',
//         documents: '',
//         jobPreference: '',
//         date: '',
//         photo: null,
//         signature: null,
//         // Tab 2
//         declarationName: '',
//         declarationChildName: '',
//         declarationCourse: '',
//         declarationDate: '',
//         declarationSignature: null,
//         // Tab 3
//         centre: "",
//         referredBy: "",
//         regNo: "",
//         batchNo: "",
//         docsSubmitted: "",
//         screenSchedule: "",
//         officeDate: "",
//         inChargeName: "",
//         inChargeSignature: null,

//         approver: "",
//         status: "Pending",
//         remark: "",
//     });

//     // Load from localStorage on mount
//     useEffect(() => {
//         const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
//         if (saved) {
//             setFormData(JSON.parse(saved));
//         }
//     }, []);

//     const handleChange = (e) => {
//         const { name, value, files, type } = e.target;
//         if (files) {
//             setFormData(prev => ({
//                 ...prev,
//                 [name]: files[0]
//             }));
//         } else {
//             setFormData(prev => ({
//                 ...prev,
//                 [name]: value
//             }));
//         }
//     };

//     const handleSave = () => {
//         localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(formData));
//         alert(`✅ Saved: ${TABS[currentTab]}`);
//     };



//     const handleFinalSubmit = async () => {
//         try {
//             // Upload a single file to Firebase Storage and return the URL
//             const uploadFileAndGetURL = async (file, folder = "form_uploads") => {
//                 if (!file) return null;
//                 const storageRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
//                 const uploadTask = await uploadBytesResumable(storageRef, file);
//                 return await getDownloadURL(uploadTask.ref);
//             };

//             // Show loading indicator
//             alert("⏳ Uploading files, please wait...");

//             // Upload files
//             const [photoURL, signatureURL, declarationSignatureURL, inChargeSignatureURL] = await Promise.all([
//                 uploadFileAndGetURL(formData.photo),
//                 uploadFileAndGetURL(formData.signature),
//                 uploadFileAndGetURL(formData.declarationSignature),
//                 uploadFileAndGetURL(formData.inChargeSignature)
//             ]);

//             // Construct final payload
//             const payload = {
//                 ...formData,
//                 photo: photoURL,
//                 signature: signatureURL,
//                 declarationSignature: declarationSignatureURL,
//                 inChargeSignature: inChargeSignatureURL,
//                 createdAt: serverTimestamp(),
//                 updatedAt: serverTimestamp()
//             };

//             // Remove file objects to avoid redundancy
//             delete payload.photoFile;
//             delete payload.signatureFile;
//             delete payload.declarationSignatureFile;
//             delete payload.inChargeSignatureFile;

//             // Save to Firestore
//             await addDoc(collection(db, "registration_form_data"), payload);

//             // alert("✅ Final Submit done. Data saved to Firestore.");
//             localStorage.removeItem(LOCAL_STORAGE_KEY);

//             // Reset form
//             setFormData({
//                 applicantName: '',
//                 course: '',
//                 batch: '',
//                 dob: '',
//                 doa: '',
//                 age: '',
//                 gender: '',
//                 qualification: '',
//                 contact1: '',
//                 address: '',
//                 fatherName: '',
//                 contact2: '',
//                 fatherOccupation: '',
//                 motherName: '',
//                 motherOccupation: '',
//                 familySize: '',
//                 familyType: '',
//                 income: '',
//                 group: '',
//                 documents: '',
//                 jobPreference: '',
//                 date: '',
//                 photo: null,
//                 signature: null,
//                 declarationName: '',
//                 declarationChildName: '',
//                 declarationCourse: '',
//                 declarationDate: '',
//                 declarationSignature: null,
//                 centre: '',
//                 referredBy: '',
//                 regNo: '',
//                 batchNo: '',
//                 docsSubmitted: '',
//                 screenSchedule: '',
//                 officeDate: '',
//                 inChargeName: '',
//                 inChargeSignature: null,
//                 approver: "",
//                 status: "Pending",
//                 remark: "",
//             });
//             setCurrentTab(0);
//         } catch (error) {
//             console.error("Error during final submit:", error);
//             alert("❌ Error submitting form. Please try again.");
//         }
//     };
//     const handleNext = () => {
//         if (currentTab < TABS.length - 1) setCurrentTab(prev => prev + 1);
//     };

//     const handlePrev = () => {
//         if (currentTab > 0) setCurrentTab(prev => prev - 1);
//     };

//     const inputStyle = { width: "100%", padding: "10px", marginBottom: "20px", boxSizing: "border-box" };
//     const labelStyle = { marginBottom: "5px", display: "block", fontWeight: 500 };

//     return (
//         <div style={{ maxWidth: "auto", margin: '0 auto', padding: 20, fontFamily: 'Arial, sans-serif' }}>
//             <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
//                 {TABS.map((tab, idx) => (
//                     <button key={idx}
//                         onClick={() => setCurrentTab(idx)}
//                         style={{
//                             flex: 1,
//                             padding: "10px 20px",
//                             background: currentTab === idx ? "#4A90E2" : "#f0f0f0",
//                             color: currentTab === idx ? "#fff" : "#333",
//                             border: "none",
//                             cursor: "pointer",
//                             fontWeight: currentTab === idx ? "bold" : "normal",
//                             borderRadius: 4
//                         }}>
//                         {tab}
//                     </button>
//                 ))}
//             </div>

//             <div className="">
//                 {currentTab === 0 && (
//                     <div className="card p-4 shadow-sm">
//                         <h4 className="mb-4">Application Form</h4>
//                         <div className="row g-3">

//                             {/* <div className="col-md-4">
//                                 <label htmlFor="date" style={{ display: "flex" }}>Session Date</label>
//                                 <input type="text" name="date" id="date" value={formData.date} onChange={handleChange} className="form-control" />
//                             </div> */}

//                             <div className="col-md-4">
//                                 <label htmlFor="applicantName" style={{ display: "flex" }}>Applicant Name</label>
//                                 <input type="text" name="applicantName" id="applicantName" value={formData.applicantName} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="course" style={{ display: "flex" }}>Course Applied For</label>
//                                 <select name="course" id="course" value={formData.course} onChange={handleChange} className="form-select">
//                                     <option value="">Select Course</option>
//                                     <option>Retail Sales Assistant</option>
//                                     <option>Warehouse Associate</option>
//                                     <option>Digital Mitra</option>
//                                     <option>IT Customer Care Executive</option>
//                                 </select>
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="batch" style={{ display: "flex" }}>Preferred Batch</label>
//                                 <select name="batch" id="batch" value={formData.batch} onChange={handleChange} className="form-select">
//                                     <option value="">Select Batch</option>
//                                     <option>Morning</option>
//                                     <option>Afternoon</option>
//                                 </select>
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="dob" style={{ display: "flex" }}>Date of Birth</label>
//                                 <input type="date" name="dob" id="dob" value={formData.dob} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="age" style={{ display: "flex" }}>Age</label>
//                                 <input type="number" name="age" id="age" value={formData.age} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="gender" style={{ display: "flex" }}>Gender</label>
//                                 <select name="gender" id="gender" value={formData.gender} onChange={handleChange} className="form-select">
//                                     <option value="">Select Gender</option>
//                                     <option>Male</option>
//                                     <option>Female</option>
//                                     <option>Other</option>
//                                 </select>
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="qualification" style={{ display: "flex" }}>Qualification</label>
//                                 <input type="text" name="qualification" id="qualification" value={formData.qualification} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="contact1" style={{ display: "flex" }}>Contact No.</label>
//                                 <input type="text" name="contact1" id="contact1" value={formData.contact1} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="address" style={{ display: "flex" }}>Address</label>
//                                 <input type="text" name="address" id="address" value={formData.address} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="fatherName" style={{ display: "flex" }}>Father’s Name</label>
//                                 <input type="text" name="fatherName" id="fatherName" value={formData.fatherName} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="contact2" style={{ display: "flex" }}>Father’s Contact</label>
//                                 <input type="text" name="contact2" id="contact2" value={formData.contact2} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="fatherOccupation" style={{ display: "flex" }}>Father’s Occupation</label>
//                                 <input type="text" name="fatherOccupation" id="fatherOccupation" value={formData.fatherOccupation} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="motherName" style={{ display: "flex" }}>Mother’s Name</label>
//                                 <input type="text" name="motherName" id="motherName" value={formData.motherName} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="motherOccupation" style={{ display: "flex" }}>Mother’s Occupation</label>
//                                 <input type="text" name="motherOccupation" id="motherOccupation" value={formData.motherOccupation} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="familySize" style={{ display: "flex" }}>Family Size</label>
//                                 <input type="number" name="familySize" id="familySize" value={formData.familySize} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="familyType" style={{ display: "flex" }}>Type of Family</label>
//                                 <select name="familyType" id="familyType" value={formData.familyType} onChange={handleChange} className="form-select">
//                                     <option value="">Select Type</option>
//                                     <option>Nuclear</option>
//                                     <option>Joint</option>
//                                 </select>
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="income" style={{ display: "flex" }}>Monthly Income</label>
//                                 <input type="text" name="income" id="income" value={formData.income} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="group" style={{ display: "flex" }}>Social Group</label>
//                                 <select name="group" id="group" value={formData.group} onChange={handleChange} className="form-select">
//                                     <option value="">Select</option>
//                                     <option>SC</option>
//                                     <option>ST</option>
//                                     <option>OBC</option>
//                                     <option>General</option>
//                                     <option>Others</option>
//                                 </select>
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="documents" style={{ display: "flex" }}>Documents Attached</label>
//                                 <select name="documents" id="documents" value={formData.documents} onChange={handleChange} className="form-select">
//                                     <option value="">Select</option>
//                                     <option>10th</option>
//                                     <option>12th</option>
//                                     <option>Graduation</option>
//                                     <option>Aadhar Card</option>
//                                 </select>
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="jobPreference" style={{ display: "flex" }}>Employment on Completion</label>
//                                 <select name="jobPreference" id="jobPreference" value={formData.jobPreference} onChange={handleChange} className="form-select">
//                                     <option value="">Select</option>
//                                     <option>Yes</option>
//                                     <option>No</option>
//                                 </select>
//                             </div>


//                             <div className="col-md-4">
//                                 <label htmlFor="doa" style={{ display: "flex" }}>Date of Application</label>
//                                 <input type="date" name="doa" id="doa" value={formData.doa} onChange={handleChange} className="form-control" />
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="photo" style={{ display: "flex" }}>Upload Photo</label>
//                                 <input type="file" name="photo" id="photo" accept="image/*" onChange={handleChange} className="form-control" />
//                                 {formData.photo && <small className="text-muted">Selected: {formData.photo.name}</small>}
//                             </div>

//                             <div className="col-md-4">
//                                 <label htmlFor="signature" style={{ display: "flex" }}>Upload Signature</label>
//                                 <input type="file" name="signature" id="signature" accept="image/*" onChange={handleChange} className="form-control" />
//                                 {formData.signature && <small className="text-muted">Selected: {formData.signature.name}</small>}
//                             </div>

//                         </div>
//                     </div>
//                 )
//                 }

//                 {
//                     currentTab === 1 && (
//                         <div className="card p-4 shadow-sm">
//                             <h4 className="mb-4">Declaration from Parent</h4>
//                             <div className="row g-3">
//                                 <div className="col-md-4">
//                                     <label style={{ display: "flex" }}>Parent’s Name</label>
//                                     <input type="text" name="declarationName" value={formData.declarationName} onChange={handleChange} className="form-control" />
//                                 </div>
//                                 <div className="col-md-4">
//                                     <label style={{ display: "flex" }}>Child’s Name</label>
//                                     <input type="text" name="declarationChildName" value={formData.declarationChildName} onChange={handleChange} className="form-control" />
//                                 </div>
//                                 {/* <div className="col-md-4">
//                                 <label style={{ display: "flex" }}>Course (CSR Program)</label>
//                                 <input type="text" name="declarationCourse" value={formData.declarationCourse} onChange={handleChange} className="form-control" />
//                             </div> */}
//                                 <div className="col-md-4">
//                                     <label htmlFor="declarationCourse" style={{ display: "flex" }}>Course (CSR Program)</label>
//                                     <select name="declarationCourse" id="declarationCourse" value={formData.declarationCourse} onChange={handleChange} className="form-select">
//                                         <option value="">Select Course</option>
//                                         <option>Retail Sales Assistant</option>
//                                         <option>Warehouse Associate</option>
//                                         <option>Digital Mitra</option>
//                                         <option>IT Customer Care Executive</option>
//                                     </select>
//                                 </div>
//                                 <div className="col-md-4">
//                                     <label style={{ display: "flex" }}>Date of Declaration</label>
//                                     <input type="date" name="declarationDate" value={formData.declarationDate} onChange={handleChange} className="form-control" />
//                                 </div>
//                                 <div className="col-md-4">
//                                     <label style={{ display: "flex" }}>Upload Parent’s Signature</label>
//                                     <input type="file" name="declarationSignature" accept="image/*" onChange={handleChange} className="form-control" />
//                                     {formData.declarationSignature && <small className="text-muted">Selected: {formData.declarationSignature.name}</small>}
//                                 </div>
//                             </div>
//                         </div>
//                     )
//                 }

//                 {
//                     currentTab === 2 && (
//                         <div className="card p-4 shadow-sm">
//                             <h4 className="mb-4">FOR OFFICE USE ONLY</h4>
//                             <div className="row g-3">

//                                 {/* Training Centre */}
//                                 <div className="col-md-4">
//                                     <label style={{ display: "flex" }}>Training Centre</label>
//                                     <select
//                                         name="centre"
//                                         value={formData.centre}
//                                         onChange={handleChange}
//                                         className="form-select"
//                                     >
//                                         <option value="">Select Centre</option>
//                                         <option value="Mansa">Mansa</option>
//                                         <option value="Bhiwandi">Bhiwandi</option>
//                                         <option value="Tumkur">Tumkur</option>
//                                     </select>
//                                 </div>

//                                 {/* Referred By */}
//                                 <div className="col-md-4">
//                                     <label style={{ display: "flex" }}>Referred By</label>
//                                     <select
//                                         name="referredBy"
//                                         value={formData.referredBy}
//                                         onChange={handleChange}
//                                         className="form-select"
//                                     >
//                                         <option value="">Select</option>
//                                         <option value="Mobiliser">Mobiliser</option>
//                                         <option value="Trainer">Trainer</option>
//                                         <option value="Friend">Friend</option>
//                                     </select>
//                                 </div>

//                                 {/* Registration No */}
//                                 <div className="col-md-4">
//                                     <label style={{ display: "flex" }}>Registration No.</label>
//                                     <input
//                                         type="text"
//                                         name="regNo"
//                                         value={formData.regNo}
//                                         onChange={handleChange}
//                                         className="form-control"
//                                     />
//                                 </div>

//                                 {/* Batch No */}
//                                 <div className="col-md-4">
//                                     <label style={{ display: "flex" }}>Batch No.</label>
//                                     <input
//                                         type="text"
//                                         name="batchNo"
//                                         value={formData.batchNo}
//                                         onChange={handleChange}
//                                         className="form-control"
//                                     />
//                                 </div>

//                                 {/* Photocopy of required documents submitted */}
//                                 <div className="col-md-4">
//                                     <label style={{ display: "flex" }}>Photocopy of Required Documents Submitted</label>
//                                     <select
//                                         name="docsSubmitted"
//                                         value={formData.docsSubmitted}
//                                         onChange={handleChange}
//                                         className="form-select"
//                                     >
//                                         <option value="">Select</option>
//                                         <option value="Yes">Yes</option>
//                                         <option value="No">No</option>
//                                     </select>
//                                 </div>

//                                 {/* Screening Schedule */}
//                                 <div className="col-md-4">
//                                     <label style={{ display: "flex" }}>Screening Schedule</label>
//                                     <select
//                                         name="screenSchedule"
//                                         value={formData.screenSchedule || ""}
//                                         onChange={handleChange}
//                                         className="form-select"
//                                     >
//                                         <option value="">Select</option>
//                                         <option value="with applicant">With Applicant</option>
//                                         <option value="with parents">With Parents</option>
//                                     </select>
//                                 </div>

//                                 {/* Office Date */}
//                                 <div className="col-md-4">
//                                     <label style={{ display: "flex" }}>Office Date</label>
//                                     <input
//                                         type="date"
//                                         name="officeDate"
//                                         value={formData.officeDate}
//                                         onChange={handleChange}
//                                         className="form-control"
//                                     />
//                                 </div>

//                                 {/* Centre In‑Charge Name */}
//                                 <div className="col-md-4">
//                                     <label style={{ display: "flex" }}>Centre In‑Charge Name</label>
//                                     <input
//                                         type="text"
//                                         name="inChargeName"
//                                         value={formData.inChargeName || ""}
//                                         onChange={handleChange}
//                                         className="form-control"
//                                     />
//                                 </div>

//                                 {/* Upload Centre In‑Charge Signature */}
//                                 <div className="col-md-4">
//                                     <label style={{ display: "flex" }}>Upload Centre In‑Charge Signature</label>
//                                     <input
//                                         type="file"
//                                         name="inChargeSignature"
//                                         accept="image/*"
//                                         onChange={handleChange}
//                                         className="form-control"
//                                     />
//                                     {formData.inChargeSignature && (
//                                         <small className="text-muted">
//                                             Selected: {formData.inChargeSignature.name || "1 file uploaded"}
//                                         </small>
//                                     )}
//                                 </div>

//                             </div>
//                         </div>

//                     )
//                 }

//             </div >


//             {/* Navigation Buttons */}
//             < div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
//                 {currentTab > 0 && (
//                     <button type="button" onClick={handlePrev} style={{ padding: "10px 20px", background: "#6c757d", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>
//                         Previous
//                     </button>
//                 )}
//                 <button type="button" onClick={handleSave} style={{ padding: "10px 20px", background: "#007bff", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>
//                     Save
//                 </button>
//                 {
//                     currentTab < TABS.length - 1 ? (
//                         <button type="button" onClick={handleNext} style={{ padding: "10px 20px", background: "#17a2b8", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>
//                             Next
//                         </button>
//                     ) : (
//                         <button type="button" onClick={handleFinalSubmit} style={{ padding: "10px 20px", background: "#28a745", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>
//                             Final Submit
//                         </button>
//                     )
//                 }
//             </div >
//         </div >
//     );
// };

// export default TabForm;